import type { LookbackMatch } from './types'

export type ContactState = {
  id: string
  name: string
  archived: boolean
  pipelineIds: string[]
}

/** The matched source as it stands TODAY, not as it was when the event fired. */
export type SourceState = { type?: string | null; status?: string | null; date?: string | null; time?: string | null }

export type LookbackRow = {
  match: LookbackMatch
  contact: ContactState | null
  source: SourceState | null
  inOpenRun: boolean
  inTargetPipeline: boolean
  /** Why the workflow may not reach this person, or null. Shown on the row. */
  blockedReason: string | null
}

export type LookbackChip = `notEnrolled` | `hideClosed`

type CurrentState = {
  contacts: ContactState[]
  sources: Record<string, SourceState>
  openRunContactIds: Set<string>
  targetPipelineId?: string
}

export function buildRows(matches: LookbackMatch[], state: CurrentState): LookbackRow[] {
  const contactById = new Map(state.contacts.map((contact) => [contact.id, contact]))
  return matches.map((match) => {
    const contact = (match.contactId && contactById.get(match.contactId)) || null
    const inOpenRun = !!contact && state.openRunContactIds.has(contact.id)
    return {
      match,
      contact,
      source: state.sources[match.sourceId] ?? null,
      inOpenRun,
      inTargetPipeline: !!contact && !!state.targetPipelineId && contact.pipelineIds.includes(state.targetPipelineId),
      blockedReason: !contact
        ? `No contact is linked to this`
        : contact.archived
          ? `Archived`
          : inOpenRun
            ? `Already in this workflow`
            : null,
    }
  })
}

const CLOSED_STATUSES = new Set([`completed`, `cancelled`, `broken`])

/** Chips narrow what the author is looking at. They never change what the engine matched. */
export function applyChips(rows: LookbackRow[], chips: Set<LookbackChip>): LookbackRow[] {
  return rows.filter(
    (row) =>
      (!chips.has(`notEnrolled`) || (!row.inOpenRun && !row.inTargetPipeline)) &&
      (!chips.has(`hideClosed`) || !CLOSED_STATUSES.has((row.source?.status ?? ``).toLowerCase())),
  )
}

type PersonGroup = { key: string; name: string; rows: LookbackRow[] }

/** One group per person, in first-seen order. Unlinked matches each stand alone: nothing says two
 *  of them are the same person. */
export function groupByPerson(rows: LookbackRow[]): PersonGroup[] {
  const groups = new Map<string, PersonGroup>()
  for (const row of rows) {
    const key = row.contact?.id ?? `unlinked:${row.match.id}`
    const group = groups.get(key) ?? { key, name: row.contact?.name ?? `No linked contact`, rows: [] }
    group.rows.push(row)
    groups.set(key, group)
  }
  return [...groups.values()]
}
